import { getCurrentTheme } from './theme.js';
import { getExerciseAnimation } from './exerciseAnimations.js';

let handlers = {};
let appContainer = null;
let exercises = {};

// SVG icons for theme toggle
const sunIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`;
const moonIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;

/**
 * Load exercise descriptions
 */
export async function loadExercises() {
  try {
    const res = await fetch('/data/exercises.json');
    exercises = await res.json();
  } catch (e) {
    console.warn('Could not load exercise descriptions:', e);
  }
}

/**
 * Initialize the UI with event handlers
 */
export function initUI({ onNext, onPrev, onToday, onThemeToggle }) {
  handlers = { onNext, onPrev, onToday, onThemeToggle };
  appContainer = document.getElementById('app');
  renderShell();
}

/**
 * Render the app shell (header, main content area, navigation)
 */
function renderShell() {
  appContainer.innerHTML = `
    <header class="app-header">
      <h1 class="app-title">SFRE Prep FY2026</h1>
      <button id="theme-toggle" class="icon-btn" aria-label="Toggle theme">
        <span class="theme-icon">${getCurrentTheme() === 'dark' ? sunIcon : moonIcon}</span>
      </button>
    </header>

    <main class="main-content">
      <div id="workout-container" class="workout-card">
        <div class="loading">Loading...</div>
      </div>
    </main>

    <nav class="nav-bar">
      <button id="prev-btn" class="nav-btn" aria-label="Previous day">
        <span class="nav-icon">←</span>
        <span class="nav-label">Previous</span>
      </button>
      <button id="today-btn" class="nav-btn nav-btn-primary" aria-label="Go to today">
        <span class="nav-label">Today</span>
      </button>
      <button id="next-btn" class="nav-btn" aria-label="Next day">
        <span class="nav-label">Next</span>
        <span class="nav-icon">→</span>
      </button>
    </nav>

    <div id="modal-overlay" class="modal-overlay" hidden>
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div class="modal-header">
          <h3 id="modal-title" class="modal-title"></h3>
          <button id="modal-close" class="modal-close" aria-label="Close">&times;</button>
        </div>
        <div id="modal-body" class="modal-body"></div>
      </div>
    </div>
  `;

  // Attach event listeners
  document.getElementById('theme-toggle').addEventListener('click', () => {
    handlers.onThemeToggle();
    updateThemeIcon();
  });

  document.getElementById('prev-btn').addEventListener('click', handlers.onPrev);
  document.getElementById('next-btn').addEventListener('click', handlers.onNext);
  document.getElementById('today-btn').addEventListener('click', handlers.onToday);

  // Modal close handlers
  document.getElementById('modal-close').addEventListener('click', closeModal);
  document.getElementById('modal-overlay').addEventListener('click', (e) => {
    if (e.target.id === 'modal-overlay') closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeModal();
  });
}

/**
 * Update the theme toggle icon
 */
function updateThemeIcon() {
  const icon = document.querySelector('.theme-icon');
  if (icon) {
    icon.innerHTML = getCurrentTheme() === 'dark' ? sunIcon : moonIcon;
  }
}

/**
 * Render the workout day content
 */
export function renderWorkoutDay(day, { isFirst, isLast }) {
  const container = document.getElementById('workout-container');

  // Format the date for display
  const dateObj = new Date(day.date + 'T00:00:00');
  const displayDate = dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  container.innerHTML = `
    <div class="workout-header">
      <div class="workout-week">Week ${day.weekNumber}</div>
      <div class="workout-date">${displayDate}</div>
    </div>
    <h2 class="workout-title">${day.workoutTitle}</h2>
    <div class="workout-description">${formatDescription(day.description)}</div>
  `;

  // Attach click handlers to exercise links
  container.querySelectorAll('.exercise-link').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const exerciseKey = link.dataset.exercise;
      showExerciseModal(exerciseKey);
    });
  });

  // Update navigation button states
  const prevBtn = document.getElementById('prev-btn');
  const nextBtn = document.getElementById('next-btn');

  prevBtn.disabled = isFirst;
  prevBtn.setAttribute('aria-disabled', isFirst);

  nextBtn.disabled = isLast;
  nextBtn.setAttribute('aria-disabled', isLast);
}

/**
 * Show the exercise modal
 */
function showExerciseModal(exerciseKey) {
  const exercise = exercises[exerciseKey];
  const modalBody = document.getElementById('modal-body');
  const animation = getExerciseAnimation(exerciseKey);

  if (!exercise) {
    // Show generic modal if exercise not found
    document.getElementById('modal-title').textContent = exerciseKey;
    let bodyHtml = '';
    if (animation) {
      bodyHtml += `<div class="exercise-animation-container">${animation}</div>`;
    }
    bodyHtml += '<p class="exercise-description">No detailed description available for this exercise.</p>';
    modalBody.innerHTML = bodyHtml;
  } else {
    document.getElementById('modal-title').textContent = exercise.name;

    let bodyHtml = '';

    // Add SVG animation if available (preferred), otherwise GIF
    if (animation) {
      bodyHtml += `<div class="exercise-animation-container">${animation}</div>`;
    } else if (exercise.gifUrl) {
      bodyHtml += `<div class="exercise-gif-container">
        <img src="${exercise.gifUrl}" alt="${exercise.name} demonstration" class="exercise-gif" loading="lazy" />
      </div>`;
    }

    bodyHtml += `<p class="exercise-description">${exercise.description}</p>`;
    modalBody.innerHTML = bodyHtml;
  }

  const overlay = document.getElementById('modal-overlay');
  overlay.hidden = false;
  document.body.style.overflow = 'hidden';
  document.getElementById('modal-close').focus();
}

/**
 * Close the modal
 */
function closeModal() {
  const overlay = document.getElementById('modal-overlay');
  if (!overlay.hidden) {
    overlay.hidden = true;
    document.body.style.overflow = '';
  }
}

/**
 * Format the workout description (convert markdown-like syntax to HTML)
 */
function formatDescription(description) {
  if (!description) return '';

  // Split into lines
  const lines = description.split('\n');
  let html = '';
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith('- ')) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      const content = trimmed.slice(2);
      html += `<li>${formatInlineText(content)}</li>`;
    } else if (trimmed.startsWith('  - ')) {
      const content = trimmed.slice(4);
      html += `<li class="nested">${formatInlineText(content)}</li>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      if (trimmed) {
        html += `<p>${formatInlineText(trimmed)}</p>`;
      }
    }
  }

  if (inList) {
    html += '</ul>';
  }

  return html;
}

/**
 * Format inline text - convert exercise names to clickable links
 */
function formatInlineText(text) {
  // Convert **text** to <strong>text</strong>
  let result = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Exercise patterns to match (order matters - longer patterns first)
  const exercisePatterns = [
    // Multi-word patterns first
    { pattern: /\b(Hand-release push-?ups?|HR push-?ups?|hand-release)\b/gi, key: 'hand-release pushups' },
    { pattern: /\b(Romanian deadlifts?|RDLs?)\b/gi, key: 'romanian deadlift' },
    { pattern: /\b(Single[- ]leg glute bridges?)\b/gi, key: 'single leg glute bridges' },
    { pattern: /\b(Single[- ]leg RDLs?)\b/gi, key: 'single-leg rdls' },
    { pattern: /\b(Bulgarian split squats?)\b/gi, key: 'bulgarian split squats' },
    { pattern: /\b(Turkish get-?ups?)\b/gi, key: 'turkish get-ups' },
    { pattern: /\b(Overhead Press)\b/gi, key: 'overhead press' },
    { pattern: /\b(Bench Press)\b/gi, key: 'bench press' },
    { pattern: /\b(Back Squats?)\b/gi, key: 'back squat' },
    { pattern: /\b(Lat Pulldowns?)\b/gi, key: 'lat pulldowns' },
    { pattern: /\b(Barbell Rows?)\b/gi, key: 'barbell rows' },
    { pattern: /\b(Leg Press)\b/gi, key: 'leg press' },
    { pattern: /\b(Skull Crushers?)\b/gi, key: 'skull crushers' },
    { pattern: /\b(Hammer Curls?)\b/gi, key: 'hammer curls' },
    { pattern: /\b(Farmers Carries?|Farmers? Carries?|Farmer'?s Carries?)\b/gi, key: 'farmers carries' },
    { pattern: /\b(Sled Drags?)\b/gi, key: 'sled drags' },
    { pattern: /\b(Sled Pushes?)\b/gi, key: 'sled pushes' },
    { pattern: /\b(Sled Work)\b/gi, key: 'sled drags' },
    { pattern: /\b(Box step-?ups?)\b/gi, key: 'box step-ups' },
    { pattern: /\b(Mountain climbers?)\b/gi, key: 'mountain climbers' },
    { pattern: /\b(Band face pulls?)\b/gi, key: 'band face pulls' },
    { pattern: /\b(Band rows?|Bands? rows?)\b/gi, key: 'band rows' },
    { pattern: /\b(Hollow holds?)\b/gi, key: 'hollow hold' },
    { pattern: /\b(Side planks?)\b/gi, key: 'side plank' },
    { pattern: /\b(Air squats?)\b/gi, key: 'air squats' },
    { pattern: /\b(Bird dogs?)\b/gi, key: 'bird dogs' },
    { pattern: /\b(Cat-?cow)\b/gi, key: 'cat-cow' },
    { pattern: /\b(Cobra stretch)\b/gi, key: 'cobra stretch' },
    { pattern: /\b(T-?pushups?|T-?Push-?ups?)\b/gi, key: 't-pushups' },
    { pattern: /\b(Scap CARs?|Scapular CARs?)\b/gi, key: 'scap cars' },
    { pattern: /\b(Weighted Lunges?)\b/gi, key: 'lunges' },
    { pattern: /\b(Walking Lunges?)\b/gi, key: 'lunges' },
    // Single word patterns
    { pattern: /\b(Step-?ups?)\b/gi, key: 'step-ups' },
    { pattern: /\b(Deadlifts?)\b/gi, key: 'deadlift' },
    { pattern: /\b(Pull-?ups?)\b/gi, key: 'pull-ups' },
    { pattern: /\b(Push-?ups?|Pushups?)\b/gi, key: 'pushups' },
    { pattern: /\b(Lunges?)\b/gi, key: 'lunges' },
    { pattern: /\b(Sit-?ups?)\b/gi, key: 'sit-ups' },
    { pattern: /\b(Planks?)\b/gi, key: 'plank' },
    { pattern: /\b(Squats?)\b/gi, key: 'back squat' },
    { pattern: /\b(Swim|Swimming)\b/gi, key: 'swim' },
    { pattern: /\b(Strides?)\b/gi, key: 'strides' },
    // Running/distance patterns
    { pattern: /\b(\d+\s*[×x]\s*400\s*m|\b400m)\b/gi, key: '400m' },
    { pattern: /\b(\d+\s*[×x]\s*800\s*m|\b800m)\b/gi, key: '800m' },
    { pattern: /\b(\d+\s*[×x]\s*200\s*m|\b200m)\b/gi, key: '200m' },
    { pattern: /\b(\d+\s*[×x]\s*100\s*m|100m strides?)\b/gi, key: '200m' },
    { pattern: /\b(tempo run|Tempo)\b/gi, key: 'tempo run' },
    { pattern: /\b(Sprint Protocol|sprint|sprints)\b/gi, key: 'sprint' },
    { pattern: /\b(core circuit)\b/gi, key: 'core circuit' },
    { pattern: /\b(warm-?up)\b/gi, key: 'warm-up' },
    { pattern: /\b(Zone 2|zone 2)\b/g, key: 'zone 2' },
    { pattern: /\b(easy run|Easy run|Easy Jog|easy jog)\b/gi, key: 'easy run' },
    // Ruck patterns
    { pattern: /\b(long ruck|Long ruck)\b/gi, key: 'ruck' },
    { pattern: /\b(heavy ruck|Heavy ruck)\b/gi, key: 'ruck' },
    { pattern: /\b(fast ruck|Fast ruck)\b/gi, key: 'ruck' },
    { pattern: /\b(medium ruck|Medium ruck)\b/gi, key: 'ruck' },
    { pattern: /\b(light ruck|Light ruck)\b/gi, key: 'ruck' },
    { pattern: /\b(ruck march|Ruck march|Ruck)\b/gi, key: 'ruck' },
  ];

  // Track which parts have been converted to avoid double-processing
  const placeholders = [];
  let placeholderIndex = 0;

  for (const { pattern, key } of exercisePatterns) {
    result = result.replace(pattern, (match) => {
      const placeholder = `__EXERCISE_${placeholderIndex}__`;
      placeholders.push({
        placeholder,
        html: `<a href="#" class="exercise-link" data-exercise="${key}">${match}</a>`
      });
      placeholderIndex++;
      return placeholder;
    });
  }

  // Replace placeholders with actual HTML
  for (const { placeholder, html } of placeholders) {
    result = result.replace(placeholder, html);
  }

  return result;
}

/**
 * Export for theme change updates
 */
export { updateThemeIcon };
